import React, { useState, useRef, useEffect, useMemo } from "react";
import { countries } from "countries-list";
import getUnicodeFlagIcon from "country-flag-icons/unicode";
import { AsYouType } from "libphonenumber-js";

const countryList = Object.keys(countries)
  .map((code) => ({
    code,
    name: countries[code].name,
    dialCode: `+${countries[code].phone[0]}`,
    flag: getUnicodeFlagIcon(code),
  }))
  .sort((a, b) => a.name.localeCompare(b.name));

const DEFAULT = countryList.find((c) => c.code === "US");

const ChevronIcon = ({ open }) => (
  <svg
    width="12" height="12" viewBox="0 0 24 24" fill="none"
    stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"
    style={{ transition: "transform 0.2s ease", transform: open ? "rotate(180deg)" : "rotate(0deg)", flexShrink: 0 }}
  >
    <polyline points="6 9 12 15 18 9" />
  </svg>
);

const SearchIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none"
    stroke="#aaa" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
    style={{ flexShrink: 0 }}>
    <circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" />
  </svg>
);

const LISTBOX_ID = "lphi-listbox";

const LoginPhoneInput = ({ onChange, placeholder, disabled }) => {
  const [selected, setSelected] = useState(DEFAULT);
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [phoneValue, setPhoneValue] = useState("");
  const [activeIndex, setActiveIndex] = useState(-1);
  const wrapperRef = useRef(null);
  const searchRef = useRef(null);
  const phoneRef = useRef(null);
  const listRef = useRef(null);

  useEffect(() => {
    const handleOutside = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setIsOpen(false);
        setSearch("");
      }
    };
    document.addEventListener("mousedown", handleOutside);
    return () => document.removeEventListener("mousedown", handleOutside);
  }, []);

  useEffect(() => {
    if (isOpen && searchRef.current) {
      searchRef.current.focus();
      setActiveIndex(-1);
    }
  }, [isOpen]);

  // Reset keyboard focus when search changes
  useEffect(() => {
    setActiveIndex(-1);
  }, [search]);

  // Scroll keyboard-focused item into view
  useEffect(() => {
    if (activeIndex >= 0 && listRef.current) {
      const item = listRef.current.children[activeIndex];
      item?.scrollIntoView({ block: "nearest" });
    }
  }, [activeIndex]);

  const filtered = useMemo(() => {
    if (!search.trim()) return countryList;
    const q = search.toLowerCase();
    return countryList.filter(
      (c) => c.name.toLowerCase().includes(q) || c.dialCode.includes(q)
    );
  }, [search]);

  const emitChange = (countryCode, rawInput) => {
    const asYouType = new AsYouType(countryCode);
    asYouType.input(rawInput);
    const phoneNumberObject = asYouType.getNumber();
    if (phoneNumberObject) {
      onChange(phoneNumberObject.number, `+${phoneNumberObject.countryCallingCode}`);
    }
  };

  const handleSelect = (country) => {
    setSelected(country);
    setIsOpen(false);
    setSearch("");
    emitChange(country.code, phoneValue);
    setTimeout(() => phoneRef.current?.focus(), 50);
  };

  const handleSearchKeyDown = (e) => {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActiveIndex((i) => Math.min(i + 1, filtered.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActiveIndex((i) => Math.max(i - 1, 0));
    } else if (e.key === "Enter") {
      e.preventDefault();
      if (activeIndex >= 0 && filtered[activeIndex]) {
        handleSelect(filtered[activeIndex]);
      }
    } else if (e.key === "Escape") {
      setIsOpen(false);
      setSearch("");
    }
  };

  const handleTriggerKeyDown = (e) => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      if (!disabled) setIsOpen((v) => !v);
    } else if (e.key === "Escape") {
      setIsOpen(false);
      setSearch("");
    }
  };

  const handlePhoneChange = (e) => {
    const asYouType = new AsYouType(selected.code);
    asYouType.input(e.target.value);
    const formatted = asYouType.input("");
    setPhoneValue(formatted);
    emitChange(selected.code, e.target.value);
  };

  const activeOptionId =
    activeIndex >= 0 && filtered[activeIndex]
      ? `lphi-option-${filtered[activeIndex].code}`
      : undefined;

  return (
    <div className="lphi-root" ref={wrapperRef}>
      {/* ── Country trigger ── */}
      <button
        type="button"
        className={`lphi-trigger${isOpen ? " lphi-trigger--open" : ""}${disabled ? " lphi-trigger--disabled" : ""}`}
        onClick={() => !disabled && setIsOpen((v) => !v)}
        onKeyDown={handleTriggerKeyDown}
        tabIndex={disabled ? -1 : 0}
        aria-haspopup="listbox"
        aria-expanded={isOpen}
        aria-label={`Country code: ${selected.name} ${selected.dialCode}`}
      >
        <span className="lphi-flag" aria-hidden="true">{selected.flag}</span>
        <span className="lphi-dialcode">{selected.dialCode}</span>
        <ChevronIcon open={isOpen} />
      </button>

      {/* ── Dropdown ── */}
      {isOpen && (
        <div className="lphi-dropdown">
          <div className="lphi-search-wrap">
            <SearchIcon />
            <input
              ref={searchRef}
              className="lphi-search"
              placeholder="Search country…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              onKeyDown={handleSearchKeyDown}
              role="combobox"
              aria-expanded="true"
              aria-autocomplete="list"
              aria-controls={LISTBOX_ID}
              aria-activedescendant={activeOptionId}
              aria-label="Search countries"
            />
          </div>
          <ul
            className="lphi-list"
            id={LISTBOX_ID}
            role="listbox"
            aria-label="Countries"
            ref={listRef}
          >
            {filtered.map((country, idx) => (
              <li
                key={country.code}
                id={`lphi-option-${country.code}`}
                className={`lphi-item${country.code === selected.code ? " lphi-item--active" : ""}${idx === activeIndex ? " lphi-item--focused" : ""}`}
                role="option"
                aria-selected={country.code === selected.code}
                onMouseDown={(e) => { e.preventDefault(); handleSelect(country); }}
              >
                <span className="lphi-item-flag" aria-hidden="true">{country.flag}</span>
                <span className="lphi-item-name">{country.name}</span>
                <span className="lphi-item-code">{country.dialCode}</span>
              </li>
            ))}
            {filtered.length === 0 && (
              <li className="lphi-empty" role="option" aria-selected="false">No countries found</li>
            )}
          </ul>
        </div>
      )}

      {/* ── Phone number ── */}
      <div className={`lphi-number${disabled ? " lphi-number--disabled" : ""}`}>
        <input
          ref={phoneRef}
          type="tel"
          className="lphi-number-input"
          placeholder={placeholder || "Enter mobile no."}
          value={phoneValue}
          onChange={handlePhoneChange}
          disabled={disabled}
          maxLength={20}
        />
      </div>
    </div>
  );
};

export default LoginPhoneInput;
